/**
 * Memory Management Utilities for Import/Export
 * 
 * This module provides utilities to manage browser memory when processing
 * large files, ensuring optimal performance and preventing memory leaks.
 */

export interface MemoryInfo {
  used: number;
  total: number;
  available: number;
  percentage: number;
}

export interface FileProcessingOptions {
  chunkSize?: number;
  maxMemoryUsage?: number; // in MB
  enableGarbageCollection?: boolean;
  progressCallback?: (progress: number) => void;
}

// Default memory management settings
const DEFAULT_OPTIONS: Required<FileProcessingOptions> = {
  chunkSize: 64 * 1024, // 64KB chunks
  maxMemoryUsage: 50, // 50MB max memory usage
  enableGarbageCollection: true,
  progressCallback: () => {}
};

/**
 * Get current memory usage information (if available)
 */
export const getMemoryInfo = (): MemoryInfo | null => {
  // Check if performance.memory is available (Chrome only)
  if (typeof performance !== 'undefined' && (performance as any).memory) {
    const memory = (performance as any).memory;
    return {
      used: memory.usedJSHeapSize,
      total: memory.totalJSHeapSize,
      available: memory.jsHeapSizeLimit - memory.usedJSHeapSize,
      percentage: (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100
    };
  }
  
  return null;
};

/**
 * Check if memory usage is within safe limits
 */
export const isMemoryUsageSafe = (maxUsageMB: number = 50): boolean => {
  const memoryInfo = getMemoryInfo();
  if (!memoryInfo) return true; // Assume safe if we can't check
  
  const maxUsageBytes = maxUsageMB * 1024 * 1024;
  return memoryInfo.used < maxUsageBytes;
};

/**
 * Force garbage collection if available
 */
export const forceGarbageCollection = (): void => {
  if (typeof window !== 'undefined' && (window as any).gc) {
    (window as any).gc();
  }
};

/**
 * Process file in chunks to manage memory usage
 */
export const processFileInChunks = async (
  file: File,
  processor: (chunk: ArrayBuffer, chunkIndex: number) => Promise<any>,
  options: FileProcessingOptions = {}
): Promise<any[]> => {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const results: any[] = [];
  const chunkSize = opts.chunkSize;
  const totalChunks = Math.ceil(file.size / chunkSize);
  
  for (let i = 0; i < totalChunks; i++) {
    // Check memory usage before processing each chunk
    if (!isMemoryUsageSafe(opts.maxMemoryUsage)) {
      throw new Error(`Memory usage exceeded ${opts.maxMemoryUsage}MB limit. Please use a smaller file.`);
    }
    
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const chunk = file.slice(start, end);
    
    // Read chunk as ArrayBuffer
    const arrayBuffer = await chunk.arrayBuffer();
    
    // Process chunk
    const result = await processor(arrayBuffer, i);
    results.push(result);
    
    // Force garbage collection after each chunk if enabled
    if (opts.enableGarbageCollection) {
      forceGarbageCollection();
    }
    
    // Update progress
    const progress = ((i + 1) / totalChunks) * 100;
    opts.progressCallback(progress);
  }
  
  return results;
};

/**
 * Process text file line by line to manage memory
 */
export const processTextFileLineByLine = async (
  file: File,
  processor: (line: string, lineNumber: number) => any,
  options: FileProcessingOptions = {}
): Promise<any[]> => {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const results: any[] = [];
  
  // Read file as text
  const text = await file.text();
  const lines = text.split('\n');
  
  for (let i = 0; i < lines.length; i++) {
    // Check memory usage periodically
    if (i % 1000 === 0 && !isMemoryUsageSafe(opts.maxMemoryUsage)) {
      throw new Error(`Memory usage exceeded ${opts.maxMemoryUsage}MB limit. Please use a smaller file.`);
    }
    
    const line = lines[i];
    if (line.trim()) { // Skip empty lines
      const result = processor(line, i);
      results.push(result);
    }
    
    // Force garbage collection periodically
    if (opts.enableGarbageCollection && i % 500 === 0) {
      forceGarbageCollection();
    }
    
    // Update progress
    const progress = ((i + 1) / lines.length) * 100;
    opts.progressCallback(progress);
  }
  
  return results;
};

/**
 * Estimate memory usage for a file
 */
export const estimateMemoryUsage = (file: File): number => {
  // Base file size
  let estimatedSize = file.size;
  
  // For compressed files, estimate uncompressed size
  if (file.name.toLowerCase().endsWith('.zip')) {
    estimatedSize *= 3; // Conservative estimate
  }
  
  // Add overhead for JavaScript object processing (roughly 2-3x)
  estimatedSize *= 2.5;
  
  return estimatedSize;
};

/**
 * Check if file is safe to process based on memory estimation
 */
export const isFileSafeToProcess = (file: File, maxMemoryMB: number = 50): boolean => {
  const estimatedUsage = estimateMemoryUsage(file);
  const maxMemoryBytes = maxMemoryMB * 1024 * 1024;
  
  return estimatedUsage <= maxMemoryBytes;
};

/**
 * Create a memory-safe file processor
 */
export const createMemorySafeProcessor = (maxMemoryMB: number = 50) => {
  return {
    canProcess: (file: File) => isFileSafeToProcess(file, maxMemoryMB),
    processInChunks: (file: File, processor: (chunk: ArrayBuffer, chunkIndex: number) => Promise<any>, options?: FileProcessingOptions) =>
      processFileInChunks(file, processor, { ...options, maxMemoryUsage: maxMemoryMB }),
    processLineByLine: (file: File, processor: (line: string, lineNumber: number) => any, options?: FileProcessingOptions) =>
      processTextFileLineByLine(file, processor, { ...options, maxMemoryUsage: maxMemoryMB })
  };
};

/**
 * Monitor memory usage during processing
 */
export class MemoryMonitor {
  private maxUsageMB: number;
  private checkInterval: number;
  private intervalId: NodeJS.Timeout | null = null;
  private onExceededCallback?: () => void;

  constructor(maxUsageMB: number = 50, checkInterval: number = 1000) {
    this.maxUsageMB = maxUsageMB;
    this.checkInterval = checkInterval;
  }

  start(onExceeded?: () => void): void {
    this.onExceededCallback = onExceeded;
    this.intervalId = setInterval(() => {
      if (!isMemoryUsageSafe(this.maxUsageMB)) {
        this.stop();
        onExceeded?.();
      }
    }, this.checkInterval);
  }

  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  getCurrentUsage(): MemoryInfo | null {
    return getMemoryInfo();
  }
}

export default {
  getMemoryInfo,
  isMemoryUsageSafe,
  forceGarbageCollection,
  processFileInChunks,
  processTextFileLineByLine,
  estimateMemoryUsage,
  isFileSafeToProcess,
  createMemorySafeProcessor,
  MemoryMonitor
};
